/**
 * 테이블 관련 공통 유틸리티 함수들
 * CRUD 테이블에서 공통으로 사용되는 로직들을 모아둠
 */
import i18n from '@/plugins/i18n';

/**
 * 관리자 CRUD 표: 뷰포트/컨테이너에 맞춰 page size를 잡을 때 쓰는 기본값.
 * 가능하면 DOM 실측(호스트·크롬·행)을 쓰고, 없을 때만 아래 fallback을 쓴다.
 */
export const CRUD_TABLE_FIT_DEFAULTS = Object.freeze({
  rowHeight: 48,
  /** solvit-crud toolbar(~64) + thead(--tableHeaderHeight 52) + pagination(~52) */
  chromeHeight: 168,
  min: 5,
  max: 50,
  resizeDebounceMs: 200
});

/**
 * :root CSS 변수(px)를 숫자로 읽는다.
 * @param {string} name - 예: '--footerHeight'
 * @param {number} fallback
 * @returns {number}
 */
export function readCssPxVar(name, fallback) {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') {
    return fallback;
  }
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch (_e) {
    return fallback;
  }
}

/**
 * Admin CRUD 가용 높이 호스트(.admin-crud-fit-host)를 찾는다.
 * @param {HTMLElement|null} fromEl
 * @returns {HTMLElement|null}
 */
export function resolveCrudTableFitHost(fromEl = null) {
  if (fromEl && typeof fromEl.closest === 'function') {
    const closest = fromEl.closest('.admin-crud-fit-host');
    if (closest) return closest;
  }
  if (typeof document !== 'undefined') {
    return document.querySelector('.admin-crud-fit-host');
  }
  return null;
}

/**
 * 표 툴바·헤더·페이지네이션 높이를 DOM에서 합산한다.
 * @param {HTMLElement|null} rootEl
 * @returns {number} 0이면 실측 실패
 */
export function measureCrudTableChromeHeight(rootEl) {
  if (!rootEl || typeof rootEl.querySelector !== 'function') {
    return 0;
  }

  const toolbar =
    rootEl.querySelector('.solvit-crud-table-toolbar') ||
    rootEl.querySelector('.v-data-table .v-toolbar') ||
    rootEl.querySelector('.v-toolbar');
  const thead = rootEl.querySelector('.v-data-table thead') || rootEl.querySelector('thead');
  const paginationEl = rootEl.querySelector('.v-pagination');
  const pagination =
    paginationEl?.closest('.text-center') ||
    paginationEl?.parentElement ||
    rootEl.querySelector('.v-data-footer');

  let sum = 0;
  [toolbar, thead, pagination].forEach(el => {
    if (!el || typeof el.getBoundingClientRect !== 'function') return;
    const h = el.getBoundingClientRect().height;
    if (Number.isFinite(h) && h > 0) sum += h;
  });
  return sum;
}

/**
 * tbody 데이터 행 평균 높이. 이미지 로드 후 등 실제 행 높이 반영.
 * @param {HTMLElement|null} rootEl
 * @returns {number} 0이면 실측 실패
 */
export function measureCrudTableRowHeight(rootEl) {
  if (!rootEl || typeof rootEl.querySelectorAll !== 'function') {
    return 0;
  }
  const rows = rootEl.querySelectorAll('.v-data-table tbody tr');
  const heights = [];
  rows.forEach(tr => {
    if (
      tr.classList?.contains('v-data-table__progress') ||
      tr.classList?.contains('v-data-table__empty-wrapper')
    ) {
      return;
    }
    if (typeof tr.getBoundingClientRect !== 'function') return;
    const h = tr.getBoundingClientRect().height;
    if (Number.isFinite(h) && h > 8) heights.push(h);
  });
  if (!heights.length) return 0;
  return heights.reduce((a, b) => a + b, 0) / heights.length;
}

/**
 * Admin CRUD 표가 쓸 수 있는 세로 공간(px).
 * 우선 `.admin-crud-fit-host` clientHeight, 없으면 뷰포트 공식.
 * @param {HTMLElement|null} [fromEl]
 * @returns {number}
 */
export function measureCrudTableFitAvailableHeight(fromEl = null) {
  const host = resolveCrudTableFitHost(fromEl);
  if (host && Number.isFinite(host.clientHeight) && host.clientHeight > 0) {
    return host.clientHeight;
  }

  const vh =
    typeof window !== 'undefined' && Number.isFinite(window.innerHeight)
      ? window.innerHeight
      : 800;
  const footer = readCssPxVar('--footerHeight', 50);
  const toolbar = readCssPxVar('--toolbarHeight', 64);
  return Math.max(0, vh - footer - toolbar);
}

/**
 * 실측 가능한 메트릭을 모은다. DOM이 없으면 fallback.
 * @param {HTMLElement|null} rootEl
 * @param {Object} [options]
 * @param {number} [options.rowHeightFallback]
 * @param {number} [options.chromeHeightFallback]
 * @returns {{ availableHeight: number, chromeHeight: number, rowHeight: number }}
 */
export function resolveCrudTableFitMetrics(rootEl = null, options = {}) {
  const rowHeightFallback =
    Number(options.rowHeightFallback) || CRUD_TABLE_FIT_DEFAULTS.rowHeight;
  const chromeHeightFallback =
    Number(options.chromeHeightFallback) || CRUD_TABLE_FIT_DEFAULTS.chromeHeight;

  const availableHeight = measureCrudTableFitAvailableHeight(rootEl);
  const measuredChrome = measureCrudTableChromeHeight(rootEl);
  const measuredRow = measureCrudTableRowHeight(rootEl);

  return {
    availableHeight,
    chromeHeight: measuredChrome > 0 ? measuredChrome : chromeHeightFallback,
    rowHeight: measuredRow > 0 ? measuredRow : rowHeightFallback
  };
}

/**
 * 가용 높이로 page size(행 수)를 계산한다.
 * @param {Object} [opts]
 * @param {number} [opts.availableHeight]
 * @param {number} [opts.rowHeight]
 * @param {number} [opts.chromeHeight]
 * @param {number} [opts.min]
 * @param {number} [opts.max]
 * @returns {number}
 */
export function calcItemsPerPageToFit(opts = {}) {
  const {
    availableHeight,
    rowHeight = CRUD_TABLE_FIT_DEFAULTS.rowHeight,
    chromeHeight = CRUD_TABLE_FIT_DEFAULTS.chromeHeight,
    min = CRUD_TABLE_FIT_DEFAULTS.min,
    max = CRUD_TABLE_FIT_DEFAULTS.max
  } = opts;

  const height = Number(availableHeight);
  if (!Number.isFinite(height) || height <= 0) {
    return min;
  }

  const rh = Math.max(1, Number(rowHeight) || CRUD_TABLE_FIT_DEFAULTS.rowHeight);
  const chrome = Math.max(0, Number(chromeHeight) || 0);
  const rows = Math.floor((height - chrome) / rh);
  const lo = Math.max(1, Number(min) || CRUD_TABLE_FIT_DEFAULTS.min);
  const hi = Math.max(lo, Number(max) || CRUD_TABLE_FIT_DEFAULTS.max);
  return Math.min(hi, Math.max(lo, rows));
}

/**
 * 기본 테이블 옵션 생성
 * @param {Array} defaultSortBy - 기본 정렬 필드
 * @param {Array} defaultSortDesc - 기본 정렬 방향
 * @param {number} itemsPerPage - 페이지당 아이템 수
 * @returns {Object} 테이블 옵션 객체
 */
export const createDefaultTableOptions = (defaultSortBy = ['seq'], defaultSortDesc = [true], itemsPerPage = 10) => {
  return {
    sortBy: [...defaultSortBy],
    sortDesc: [...defaultSortDesc],
    page: 1,
    itemsPerPage
  };
};

/**
 * 정렬 파라미터 생성
 * @param {Array} sortBy - 정렬할 필드들
 * @param {Array} sortDesc - 정렬 방향들
 * @param {Object} sortableFields - 정렬 가능한 필드 매핑
 * @returns {string} API 요청용 정렬 파라미터
 */
export const buildSortParam = (sortBy, sortDesc, sortableFields = {}) => {
  if (!sortBy || sortBy.length === 0) {
    return 'seq,DESC'; // 기본 정렬
  }

  const field = sortBy[0];
  const direction = sortDesc[0] ? 'DESC' : 'ASC';

  // sortableFields에서 실제 API 필드명 찾기
  const apiField = sortableFields[field] || field;

  return `${apiField},${direction}`;
};

/**
 * 페이지네이션 파라미터 생성
 * @param {number} page - 현재 페이지 (1부터 시작)
 * @param {number} itemsPerPage - 페이지당 아이템 수
 * @returns {Object} API 요청용 페이지네이션 파라미터
 */
export const buildPaginationParams = (page, itemsPerPage) => {
  const apiPage = Math.max(0, page - 1); // API는 0부터 시작

  return {
    page: apiPage,
    size: itemsPerPage
  };
};

/**
 * API 요청 URL 생성
 * @param {string} endpoint - API 엔드포인트
 * @param {string} keyword - 검색 키워드
 * @param {Object} pagination - 페이지네이션 파라미터
 * @param {string} sort - 정렬 파라미터
 * @returns {string} 완성된 API 요청 URL
 */
export const buildApiUrl = (endpoint, keyword = '', pagination = {}, sort = '') => {
  const params = new URLSearchParams();

  if (keyword && keyword.trim()) {
    params.append('keyword', keyword.trim());
  }

  if (pagination.size) {
    params.append('size', pagination.size);
  }

  if (pagination.page !== undefined) {
    params.append('page', pagination.page);
  }

  if (sort) {
    params.append('sort', sort);
  }

  const queryString = params.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
};

/**
 * API 응답 데이터 변환
 * pagination 정보를 항상 올바르게 처리합니다 (totalElements가 0일 때도 pageCount를 1로 설정)
 * @param {Object} response - API 응답 (res.data 형식)
 * @param {Function} transformFunction - 데이터 변환 함수 (선택사항)
 * @returns {Object} 변환된 데이터 { items, pageCount, page, totalElements }
 */
export const transformApiResponse = (response, transformFunction = null) => {
  // response가 res.data 형식인지 확인 (content가 바로 있는 경우)
  const contentData = response?.content || response;
  
  if (!contentData?.page) {
    return {
      items: [],
      pageCount: 1,
      page: 1,
      totalElements: 0
    };
  }

  const pageInfo = contentData.page;
  let items = contentData.content || [];

  // 변환 함수가 있으면 적용
  if (transformFunction && typeof transformFunction === 'function') {
    items = transformFunction(items);
  }

  // totalPages가 0이어도 최소 1로 설정 (검색 결과가 없을 때도 pagination 표시)
  const pageCount = pageInfo.totalPages !== undefined && pageInfo.totalPages > 0 
    ? pageInfo.totalPages 
    : 1;

  return {
    items: Array.isArray(items) ? items : [],
    pageCount,
    page: pageInfo.number !== undefined ? pageInfo.number + 1 : 1, // API는 0부터, UI는 1부터
    totalElements: pageInfo.totalElements || 0
  };
};

/**
 * 테이블 옵션 업데이트
 * @param {Object} currentOptions - 현재 옵션
 * @param {Object} newOptions - 새로운 옵션
 * @returns {Object} 업데이트된 옵션
 */
export const updateTableOptions = (currentOptions, newOptions) => {
  return {
    ...currentOptions,
    ...newOptions
  };
};

/**
 * 정렬 해제 시 기본 정렬로 복원
 * @param {Object} options - 현재 테이블 옵션
 * @param {Array} defaultSortBy - 기본 정렬 필드
 * @param {Array} defaultSortDesc - 기본 정렬 방향
 * @returns {Object} 복원된 옵션
 */
export const restoreDefaultSort = (options, defaultSortBy, defaultSortDesc) => {
  return {
    ...options,
    sortBy: [...defaultSortBy],
    sortDesc: [...defaultSortDesc]
  };
};

/**
 * 테이블 데이터 검증
 * @param {Array} items - 테이블 아이템들
 * @param {Array} headers - 테이블 헤더들
 * @returns {boolean} 데이터 유효성
 */
export const validateTableData = (items, headers) => {
  if (!Array.isArray(items) || !Array.isArray(headers)) {
    return false;
  }

  // 헤더의 value 필드들이 모두 존재하는지 확인
  const requiredFields = headers
    .filter(header => header.value && !header.value.includes('.')) // 중첩 필드 제외
    .map(header => header.value);

  if (items.length === 0) return true; // 빈 데이터는 유효

  const firstItem = items[0];
  return requiredFields.every(field => Object.prototype.hasOwnProperty.call(firstItem, field));
};

/**
 * 공통 에러 처리
 * @param {Error} error - 에러 객체
 * @param {string} context - 에러 발생 컨텍스트
 * @returns {Object} 에러 정보
 */
export const handleTableError = (error, context = '테이블') => {
  console.error(`[${context}] 에러 발생:`, error);

  return {
    message: error.response?.data?.message || error.message || i18n.t('common.unknownError'),
    status: error.response?.status || 0,
    context
  };
};

/**
 * 검색 키워드 정규화
 * @param {string} keyword - 검색 키워드
 * @returns {string} 정규화된 키워드
 */
export const normalizeSearchKeyword = (keyword) => {
  if (!keyword) return '';

  return keyword
    .trim()
    .replace(/\s+/g, ' ') // 연속된 공백을 하나로
    .toLowerCase();
};

/**
 * 테이블 로딩 상태 관리
 * @param {boolean} isLoading - 로딩 상태
 * @param {Function} callback - 로딩 완료 후 실행할 콜백
 * @returns {Promise} 로딩 완료를 기다리는 Promise
 */
export const manageTableLoading = async (isLoading, callback) => {
  if (typeof callback !== 'function') {
    throw new Error(i18n.t('common.callbackRequired'));
  }

  return await callback();
};

/**
 * 테이블 데이터 포맷팅 유틸리티
 * 도메인에 구애받지 않는 범용 함수들
 */

/**
 * 빈 값이나 null 값을 기본값으로 변환
 * @param {*} value - 검사할 값
 * @param {*} defaultValue - 기본값 (기본값: '-')
 * @returns {*} 변환된 값
 */
export function getValueOrDefault(value, defaultValue = '-') {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return defaultValue;
  }
  return value;
}

/**
 * 객체에서 여러 속성을 순차적으로 체크하여 첫 번째 유효한 값 반환
 * @param {Object} obj - 검사할 객체
 * @param {Array<string>|string} propertyNames - 체크할 속성명 배열 또는 단일 속성명 (우선순위 순서)
 * @param {*} defaultValue - 기본값 (기본값: '-')
 * @returns {*} 첫 번째 유효한 값 또는 기본값
 */
export function getObjectPropertyOrDefault(obj, propertyNames, defaultValue = '-') {
  if (!obj || typeof obj !== 'object') {
    return defaultValue;
  }

  // 배열이 아닌 경우 단일 속성명으로 처리
  if (typeof propertyNames === 'string') {
    propertyNames = [propertyNames];
  }

  // 여러 속성을 순차적으로 체크
  for (const propName of propertyNames) {
    const value = obj[propName];
    if (value !== null && value !== undefined && value !== '') {
      if (typeof value === 'string' && value.trim() !== '') {
        return value;
      }
      if (typeof value !== 'string') {
        return value;
      }
    }
  }

  return defaultValue;
}

/**
 * 배열을 문자열로 결합하되, 비어있으면 기본값 반환
 * @param {Array} array - 검사할 배열
 * @param {Function} formatter - 각 항목을 포맷팅할 함수
 * @param {string} separator - 구분자 (기본값: ', ')
 * @param {string} defaultValue - 기본값 (기본값: '-')
 * @returns {string} 포맷팅된 값
 */
export function joinArrayOrDefault(array, formatter = (item) => item, separator = ', ', defaultValue = '-') {
  if (!array || !Array.isArray(array) || array.length === 0) {
    return defaultValue;
  }

  return array.map(formatter).join(separator);
}

/**
 * 긴 텍스트를 지정된 길이로 자르고 말줄임표 추가
 * @param {string} text - 원본 텍스트
 * @param {number} maxLength - 최대 길이 (기본값: 50)
 * @param {string} suffix - 접미사 (기본값: '...')
 * @returns {string} 잘린 텍스트
 */
export function truncateText(text, maxLength = 50, suffix = '...') {
  if (!text || typeof text !== 'string') {
    return '-';
  }

  if (text.length <= maxLength) {
    return text;
  }

  return text.substring(0, maxLength) + suffix;
}

/**
 * 날짜를 지정된 형식으로 포맷팅
 * @param {Date|string} date - 날짜
 * @param {string} format - 형식 (기본값: 'YYYY-MM-DD')
 * @param {string} defaultValue - 기본값 (기본값: '-')
 * @returns {string} 포맷팅된 날짜
 */
export function formatDateToString(date, format = 'YYYY-MM-DD', defaultValue = '-') {
  if (!date) {
    return defaultValue;
  }

  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return defaultValue;
    }

    // 간단한 날짜 포맷팅 (moment.js 사용 시 더 정교하게 가능)
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    return format
      .replace('YYYY', year)
      .replace('MM', month)
      .replace('DD', day);
  } catch (error) {
    return defaultValue;
  }
}

/**
 * 숫자를 지정된 형식으로 포맷팅
 * @param {number} number - 숫자
 * @param {number} decimals - 소수점 자릿수 (기본값: 0)
 * @param {string} defaultValue - 기본값 (기본값: '-')
 * @returns {string} 포맷팅된 숫자
 */
export function formatNumberToString(number, decimals = 0, defaultValue = '-') {
  if (number === null || number === undefined || isNaN(number)) {
    return defaultValue;
  }

  try {
    return Number(number).toFixed(decimals);
  } catch (error) {
    return defaultValue;
  }
}

/**
 * 테이블 컬럼별 커스텀 렌더링을 위한 헬퍼 함수들
 */

/**
 * 중첩된 객체 속성 값을 안전하게 가져오기
 * @param {Object} obj - 대상 객체
 * @param {string} path - 속성 경로 (예: 'location.coordinates')
 * @param {*} defaultValue - 기본값
 * @returns {*} 속성 값 또는 기본값
 */
export function getNestedValue(obj, path, defaultValue = null) {
  if (!obj || !path) return defaultValue;

  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : defaultValue;
  }, obj);
}

/**
 * 테이블 행의 고유 키 생성
 * @param {Object} item - 테이블 아이템
 * @param {string|Array} keyFields - 키로 사용할 필드들
 * @returns {string} 고유 키
 */
export function generateRowKey(item, keyFields = 'id') {
  if (typeof keyFields === 'string') {
    return String(item[keyFields] || item.id || Math.random());
  }

  if (Array.isArray(keyFields)) {
    return keyFields.map(field => item[field]).join('-') || Math.random();
  }

  return Math.random();
}

/**
 * 테이블 데이터 필터링
 * @param {Array} items - 원본 데이터
 * @param {string} keyword - 검색 키워드
 * @param {Array} searchFields - 검색할 필드들
 * @returns {Array} 필터링된 데이터
 */
export function filterTableData(items, keyword, searchFields = []) {
  if (!keyword || !searchFields.length) return items;

  const normalizedKeyword = keyword.toLowerCase().trim();

  return items.filter(item => {
    return searchFields.some(field => {
      const value = getNestedValue(item, field);
      if (value === null || value === undefined) return false;

      return String(value).toLowerCase().includes(normalizedKeyword);
    });
  });
}

/**
 * 테이블 데이터 정렬 (클라이언트 사이드)
 * @param {Array} items - 정렬할 데이터
 * @param {string} sortBy - 정렬 기준 필드
 * @param {boolean} sortDesc - 내림차순 여부
 * @returns {Array} 정렬된 데이터
 */
export function sortTableData(items, sortBy, sortDesc = false) {
  if (!sortBy || !items.length) return items;

  return [...items].sort((a, b) => {
    const aValue = getNestedValue(a, sortBy);
    const bValue = getNestedValue(b, sortBy);

    if (aValue === bValue) return 0;
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    let comparison = 0;
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      comparison = aValue.localeCompare(bValue);
    } else {
      comparison = aValue < bValue ? -1 : 1;
    }

    return sortDesc ? -comparison : comparison;
  });
}
